import {
  AddIcon,
  Button,
  DeleteIcon,
  Grid,
  GridColumn,
  GridRow,
  Input,
  ModalBottomButtons,
  Tooltip,
} from '@bp/ui-components';
import { graphql } from 'client/gql';
import {
  CreateClassGroupMutationVariables,
  CreateDivisionMutationVariables,
  DeleteClassGroupMutationVariables,
  DeleteDivisionMutationVariables,
  UpdateClassGroupMutationVariables,
  UpdateDivisionMutationVariables,
} from 'client/gql/graphql';
import { Field, FieldArray, Form, Formik, FormikHelpers, getIn } from 'formik';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { useConfirm } from 'hooks/useConfirm';
import { BpClassType } from 'pages/Institution/subpages/InstitutionClassesSubpage';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import { showErrorToast } from 'utils/showErrorToast';
import { showSuccessToast } from 'utils/showSuccessToast';
import { v4 } from 'uuid';
import styles from './DivisionsForm.module.scss';

const CreateClassGroupMutationDocument = graphql(`
  mutation CreateClassGroup($input: [ClassGroupCreateInput!]!) {
    createClassGroups(input: $input) {
      classGroups {
        uuid
        __typename
      }
    }
  }
`);
const CreateDivisionMutationDocument = graphql(`
  mutation CreateDivision($input: [DivisionCreateInput!]!) {
    createDivisions(input: $input) {
      divisions {
        uuid
        __typename
      }
    }
  }
`);
const UpdateClassGroupMutationDocument = graphql(`
  mutation UpdateClassGroup($update: ClassGroupUpdateInput!, $where: ClassGroupWhere!) {
    updateClassGroups(update: $update, where: $where) {
      classGroups {
        uuid
        __typename
        name
        shortName
      }
    }
  }
`);
const UpdateDivisionMutationDocument = graphql(`
  mutation UpdateDivision($update: DivisionUpdateInput!, $where: DivisionWhere!) {
    updateDivisions(update: $update, where: $where) {
      divisions {
        uuid
        __typename
        name
      }
    }
  }
`);
const DeleteClassGroupMutationDocument = graphql(`
  mutation DeleteClassGroup($delete: ClassGroupDeleteInput!, $where: ClassGroupWhere!) {
    deleteClassGroups(delete: $delete, where: $where) {
      nodesDeleted
    }
  }
`);
const DeleteDivisionMutationDocument = graphql(`
  mutation DeleteDivision($delete: DivisionDeleteInput!, $where: DivisionWhere!) {
    deleteDivisions(delete: $delete, where: $where) {
      nodesDeleted
    }
  }
`);

const DivisionsFormPageQueryDocument = graphql(`
  query DivisionsFormPageData($classUuid: ID!, $organizationUuid: ID!) {
    divisions(where: { class: { uuid: $classUuid }, organization: { uuid: $organizationUuid } }) {
      uuid
      __typename
      name
      groups {
        uuid
        __typename
        name
        shortName
      }
    }
  }
`);

type DivisionsFormProps = {
  bpClass: BpClassType;
  onLoading: (isLoading: boolean) => void;
  onClose: () => void;
};

type GroupFormType = {
  uuid: string;
  name: string;
  shortName: string;
  isNew: boolean;
};

type DivisionFormType = {
  uuid: string;
  name: string;
  isNew: boolean;
  groups: GroupFormType[];
};

type DivisionsFormType = {
  divisions: DivisionFormType[];
};

export const DivisionsForm = ({ bpClass, onLoading, onClose }: DivisionsFormProps) => {
  const { t } = useTranslation();
  const organizationUuid = useAuthClaims().pimAuthClaims.getOrganizationUuid();

  const [, createDivision] = useMutation(CreateDivisionMutationDocument);
  const [, updateDivision] = useMutation(UpdateDivisionMutationDocument);
  const [, deleteDivision] = useMutation(DeleteDivisionMutationDocument);
  const [, createClassGroup] = useMutation(CreateClassGroupMutationDocument);
  const [, updateClassGroup] = useMutation(UpdateClassGroupMutationDocument);
  const [, deleteClassGroup] = useMutation(DeleteClassGroupMutationDocument);

  const { ConfirmationDialog: GroupConfirmDialog, confirm: confirmGroupDelete } = useConfirm({
    defaultTitle: t('delete.headline'),
    defaultMessage: t('delete.message', { type: t('classGroups.title', { count: 1 }), context: 'female' }),
    defaultConfirmText: t('delete.delete'),
  });

  const { ConfirmationDialog: DivisionConfirmDialog, confirm: confirmDivisionDelete } = useConfirm({
    defaultTitle: t('delete.headline'),
    defaultMessage: t('delete.message', { type: t('divisions.title.singular'), context: 'female' }),
    defaultConfirmText: t('delete.delete'),
  });

  const [{ data }] = useQuery({
    query: DivisionsFormPageQueryDocument,
    variables: {
      organizationUuid,
      classUuid: bpClass.uuid,
    },
  });

  const initialValues: DivisionsFormType = {
    divisions:
      data?.divisions.map((division) => {
        const { uuid, name, groups } = division;
        return {
          uuid,
          name,
          isNew: false,
          classUuid: bpClass.uuid,
          classGroupUuids: division.groups.map((g) => g.uuid),
          groups: groups.map((g) => {
            return {
              uuid: g.uuid,
              name: g.name,
              shortName: g.shortName,
              isNew: false,
            };
          }),
        };
      }) ?? [],
  };

  function getDivisionCreate(division: DivisionFormType): CreateDivisionMutationVariables {
    return {
      input: {
        name: division.name,
        managedBy: 'bp',
        foreignRefId: [],
        class: {
          connect: {
            where: {
              node: {
                uuid: bpClass.uuid,
              },
            },
          },
        },
        organization: {
          connect: {
            where: {
              node: {
                uuid: organizationUuid,
              },
            },
          },
        },
      },
    };
  }

  function getDivisionEdit(division: DivisionFormType): UpdateDivisionMutationVariables {
    return {
      update: {
        name: division.name,
      },
      where: {
        uuid: division.uuid,
      },
    };
  }

  function getDivisionDelete(division: DivisionFormType): DeleteDivisionMutationVariables {
    return {
      delete: {
        foreignSyncMeta: [
          {
            where: {
              node: {
                syncTarget: {
                  Division: {
                    uuid: division.uuid,
                  },
                },
              },
            },
          },
        ],
        groups: division.groups.map((g) => {
          return {
            delete: {
              foreignSyncMeta: [
                {
                  where: {
                    node: {
                      syncTarget: {
                        Group: {
                          uuid: g.uuid,
                        },
                      },
                    },
                  },
                },
              ],
            },
            where: {
              node: {
                uuid: g.uuid,
              },
            },
          };
        }),
      },
      where: {
        uuid: division.uuid,
      },
    };
  }

  function getGroupCreate(
    group: GroupFormType,
    index: number,
    divisionUuid: string,
  ): CreateClassGroupMutationVariables {
    return {
      input: {
        name: group.name,
        shortName: group.shortName,
        managedBy: 'bp',
        foreignRefId: [],
        division: {
          connect: {
            edge: {
              order: index,
            },
            where: {
              node: {
                uuid: divisionUuid,
              },
            },
          },
        },
        organization: {
          connect: {
            where: {
              node: {
                uuid: organizationUuid,
              },
            },
          },
        },
      },
    };
  }

  function getGroupEdit(group: GroupFormType): UpdateClassGroupMutationVariables {
    return {
      update: {
        name: group.name,
        shortName: group.shortName,
      },
      where: {
        uuid: group.uuid,
      },
    };
  }

  function getGroupDelete(group: GroupFormType): DeleteClassGroupMutationVariables {
    return {
      delete: {
        foreignSyncMeta: [
          {
            where: {
              node: {
                syncTarget: {
                  ClassGroup: { uuid: group.uuid },
                },
              },
            },
          },
        ],
      },
      where: {
        uuid: group.uuid,
      },
    };
  }

  async function createGroups(groupsToCreate: CreateClassGroupMutationVariables[]) {
    let createFinished = 0;
    for (const input of groupsToCreate) {
      const result = await createClassGroup(input);
      if (result.error) {
        showErrorToast(result.error);
        return;
      }
      createFinished++;
    }
    if (createFinished > 0) {
      showSuccessToast(t('classGroups.groupCreated', { count: createFinished, groups: groupsToCreate.length }));
    }
  }

  async function editGroups(groupsToEdit: UpdateClassGroupMutationVariables[]) {
    let updateFinished = 0;
    for (const update of groupsToEdit) {
      const result = await updateClassGroup(update);
      if (result.error) {
        showErrorToast(result.error);
        return;
      }
      updateFinished++;
    }
    if (updateFinished > 0) {
      showSuccessToast(t('classGroups.groupEdited', { count: updateFinished, groups: groupsToEdit.length }));
    }
  }

  async function deleteGroups(groupsToDelete: DeleteClassGroupMutationVariables[]) {
    let deleteFinished = 0;
    for (const remove of groupsToDelete) {
      const result = await deleteClassGroup(remove);
      if (result.error) {
        showErrorToast(result.error);
        return;
      }
      deleteFinished++;
    }
    if (deleteFinished > 0) {
      showSuccessToast(t('classGroups.groupDeleted', { count: deleteFinished, groups: groupsToDelete.length }));
    }
  }

  const handleSubmit = async (values: DivisionsFormType, formHelpers: FormikHelpers<DivisionsFormType>) => {
    let hasError = false;

    if (values.divisions) {
      values.divisions.forEach((division, divisionIndex) => {
        if (!division.name) {
          formHelpers.setFieldError(`divisions.${divisionIndex}.name`, t('validation.required.type'));
          hasError = true;
        }
        if (!division.groups || division.groups.length === 0) {
          formHelpers.setFieldError(`divisions.${divisionIndex}.name`, t('validation.atLeastOneGroup'));
          hasError = true;
        } else {
          division.groups.forEach((group, groupIndex) => {
            if (!group.name) {
              formHelpers.setFieldError(
                `divisions.${divisionIndex}.groups.${groupIndex}.name`,
                t('validation.required.designation'),
              );
              hasError = true;
            }
            if (!group.shortName) {
              formHelpers.setFieldError(
                `divisions.${divisionIndex}.groups.${groupIndex}.shortName`,
                t('validation.required.shortName'),
              );
              hasError = true;
            }
          });
        }
      });
    } else {
      hasError = true;
    }
    if (hasError) {
      return;
    }

    onLoading(true);

    const initialDivisions = initialValues.divisions;
    const newDivisions = values.divisions;

    const divisionsToCreate: DivisionFormType[] = newDivisions.filter((division) => division.isNew);

    const divisionsToEdit: DivisionFormType[] = newDivisions.filter(
      (division) =>
        !division.isNew &&
        initialDivisions.some(
          (initialDivision) =>
            (division.uuid === initialDivision.uuid && division.name !== initialDivision.name) ||
            division.groups.length !== initialDivision.groups.length ||
            division.groups.some(
              (group) =>
                !group.isNew &&
                initialDivision.groups.some(
                  (initialGroup) =>
                    group.uuid === initialGroup.uuid &&
                    (group.name !== initialGroup.name || group.shortName !== initialGroup.shortName),
                ),
            ),
        ),
    );

    const divisionsToDelete: DivisionFormType[] = initialDivisions.filter(
      (division) => !newDivisions.some((newDivision) => division.uuid === newDivision.uuid),
    );

    for (const division of divisionsToCreate) {
      const divisionCreateResult = await createDivision(getDivisionCreate(division));

      const newDivisionUuid = divisionCreateResult.data?.createDivisions.divisions[0]?.uuid;
      if (divisionCreateResult.error) {
        showErrorToast(divisionCreateResult.error);
        return;
      } else if (!newDivisionUuid) {
        return;
      } else {
        showSuccessToast(t('divisions.divisionCreated', { name: division.name }));
      }

      const groupsToCreate: CreateClassGroupMutationVariables[] = division.groups
        .filter((group) => group.isNew)
        .map((group, groupIndex) => {
          return getGroupCreate(group, groupIndex, newDivisionUuid);
        });

      await createGroups(groupsToCreate);
    }

    for (const division of divisionsToEdit) {
      const divisionUpdateResult = await updateDivision(getDivisionEdit(division));

      if (divisionUpdateResult.error) {
        showErrorToast(divisionUpdateResult.error);
      } else {
        showSuccessToast(t('divisions.divisionEdited', { name: division.name }));
      }

      const initialGroups = initialValues.divisions.find((d) => d.uuid === division.uuid)?.groups ?? [];
      const newGroups = division.groups;

      const groupsToCreate: CreateClassGroupMutationVariables[] = division.groups
        .filter((group) => group.isNew)
        .map((group, groupIndex) => getGroupCreate(group, groupIndex, division.uuid));

      const groupsToEdit: UpdateClassGroupMutationVariables[] = newGroups
        .filter(
          (group) =>
            !group.isNew &&
            initialGroups.some(
              (initialGroup) =>
                group.uuid === initialGroup.uuid &&
                (group.name !== initialGroup.name || group.shortName !== initialGroup.shortName),
            ),
        )
        .map((group) => getGroupEdit(group));

      const groupsToDelete: DeleteClassGroupMutationVariables[] = initialGroups
        .filter((group) => !newGroups.some((newGroup) => group.uuid === newGroup.uuid))
        .map((group) => getGroupDelete(group));

      await createGroups(groupsToCreate);
      await editGroups(groupsToEdit);
      await deleteGroups(groupsToDelete);
    }

    for (const division of divisionsToDelete) {
      const groupsToDelete: DeleteClassGroupMutationVariables[] = division.groups.map((group) => getGroupDelete(group));

      await deleteGroups(groupsToDelete);

      const divisionDeleteResult = await deleteDivision(getDivisionDelete(division));

      if (divisionDeleteResult.error) {
        showErrorToast(divisionDeleteResult.error);
      } else {
        showSuccessToast(t('divisions.divisionDeleted', { name: division.name }));
      }
    }

    onLoading(false);
    formHelpers.resetForm();
    onClose();
  };

  return (
    <Formik<DivisionsFormType>
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validateOnBlur={true}
      enableReinitialize={true}
    >
      {({ errors, values, isSubmitting, touched, dirty, isValidating, handleSubmit, handleChange }) => {
        return (
          <Form className={styles['divisions-form']}>
            <Grid useFormGap>
              <FieldArray name='divisions'>
                {(arrayHelper) => {
                  return (
                    <>
                      {values.divisions.map((division, divisionIndex) => {
                        return (
                          <div key={division.uuid} className={styles['division-wrapper']}>
                            <GridRow
                              headline={`${t('divisions.title.singular')} ${divisionIndex + 1}`}
                              spacingBottom='s'
                            >
                              <GridColumn width={11}>
                                <GridRow>
                                  <GridColumn width={6}>
                                    <Field
                                      name={`${arrayHelper.name}.${divisionIndex}.name`}
                                      label={t('common.type')}
                                      value={division.name}
                                      onChange={handleChange}
                                      as={Input}
                                      error={
                                        getIn(touched, `${arrayHelper.name}.${divisionIndex}.name`) &&
                                        getIn(errors, `${arrayHelper.name}.${divisionIndex}.name`)
                                          ? getIn(errors, `${arrayHelper.name}.${divisionIndex}.name`)
                                          : undefined
                                      }
                                    />
                                  </GridColumn>
                                  <GridColumn width={6}>
                                    <Input
                                      name='bpClassName'
                                      label={t('divisions.assignedClass')}
                                      readonly
                                      value={bpClass.name}
                                    />
                                  </GridColumn>
                                </GridRow>
                              </GridColumn>
                              <GridColumn width={1} align='end'>
                                <Button
                                  className='mt-4'
                                  hierarchy='ghost'
                                  icon={
                                    division.groups.length > 0 ? (
                                      <Tooltip content={t('divisions.deleteGroupsFirst')}>
                                        <DeleteIcon />
                                      </Tooltip>
                                    ) : (
                                      <DeleteIcon />
                                    )
                                  }
                                  disabled={division.groups.length > 0}
                                  onClick={async () => {
                                    if (division.isNew) {
                                      arrayHelper.remove(divisionIndex);
                                    } else {
                                      const res = await confirmDivisionDelete();
                                      if (res) arrayHelper.remove(divisionIndex);
                                    }
                                  }}
                                />
                              </GridColumn>
                            </GridRow>
                            <div className={styles['group-wrapper']}>
                              {division.groups.map((group, groupIndex) => {
                                return (
                                  <GridRow key={group.uuid} spacingBottom='none' spacingTop='none'>
                                    <GridColumn width={11}>
                                      <GridRow>
                                        <GridColumn width={10}>
                                          <Field
                                            name={`${arrayHelper.name}.${divisionIndex}.groups.${groupIndex}.name`}
                                            label={`${t('classGroups.title', { count: 1 })} ${groupIndex + 1} ${t('common.designation')}`}
                                            value={group.name}
                                            onChange={handleChange}
                                            as={Input}
                                            error={
                                              getIn(
                                                touched,
                                                `${arrayHelper.name}.${divisionIndex}.groups.${groupIndex}.name`,
                                              ) &&
                                              getIn(
                                                errors,
                                                `${arrayHelper.name}.${divisionIndex}.groups.${groupIndex}.name`,
                                              )
                                                ? getIn(
                                                    errors,
                                                    `${arrayHelper.name}.${divisionIndex}.groups.${groupIndex}.name`,
                                                  )
                                                : undefined
                                            }
                                          />
                                        </GridColumn>
                                        <GridColumn width={2}>
                                          <Field
                                            name={`${arrayHelper.name}.${divisionIndex}.groups.${groupIndex}.shortName`}
                                            label={`${t('classGroups.title', { count: 1 })} ${groupIndex + 1} ${t('common.shortName')}`}
                                            value={group.shortName}
                                            onChange={handleChange}
                                            as={Input}
                                            error={
                                              getIn(
                                                touched,
                                                `${arrayHelper.name}.${divisionIndex}.groups.${groupIndex}.shortName`,
                                              ) &&
                                              getIn(
                                                errors,
                                                `${arrayHelper.name}.${divisionIndex}.groups.${groupIndex}.shortName`,
                                              )
                                                ? getIn(
                                                    errors,
                                                    `${arrayHelper.name}.${divisionIndex}.groups.${groupIndex}.shortName`,
                                                  )
                                                : undefined
                                            }
                                          />
                                        </GridColumn>
                                      </GridRow>
                                    </GridColumn>
                                    <GridColumn width={1} align='end'>
                                      <Button
                                        className='mt-4'
                                        hierarchy='ghost'
                                        icon={<DeleteIcon />}
                                        onClick={async () => {
                                          if (group.isNew) {
                                            const groups = values.divisions[divisionIndex].groups.filter(
                                              (g) => g.uuid !== group.uuid,
                                            );
                                            arrayHelper.replace(divisionIndex, {
                                              ...values.divisions[divisionIndex],
                                              groups,
                                            });
                                          } else {
                                            const res = await confirmGroupDelete();
                                            if (res) {
                                              const groups = values.divisions[divisionIndex].groups.filter(
                                                (g) => g.uuid !== group.uuid,
                                              );
                                              arrayHelper.replace(divisionIndex, {
                                                ...values.divisions[divisionIndex],
                                                groups,
                                              });
                                            }
                                          }
                                        }}
                                      />
                                    </GridColumn>
                                  </GridRow>
                                );
                              })}
                            </div>

                            <GridRow spacingTop='s'>
                              <Button
                                hierarchy='tertiary'
                                icon={<AddIcon />}
                                onClick={() => {
                                  const groups = values.divisions[divisionIndex].groups;
                                  groups.push({
                                    uuid: v4(),
                                    name: '',
                                    shortName: '',
                                    isNew: true,
                                  });
                                  arrayHelper.replace(divisionIndex, {
                                    ...values.divisions[divisionIndex],
                                    groups,
                                  });
                                }}
                              >
                                {t('classGroups.addGroup')}
                              </Button>
                            </GridRow>
                          </div>
                        );
                      })}

                      <GridRow>
                        <Button
                          hierarchy='tertiary'
                          icon={<AddIcon />}
                          onClick={() => {
                            arrayHelper.push({
                              uuid: v4(),
                              name: '',
                              groups: [],
                              isNew: true,
                            });
                          }}
                        >
                          {t('divisions.addDivision')}
                        </Button>
                      </GridRow>
                    </>
                  );
                }}
              </FieldArray>
            </Grid>

            <ModalBottomButtons
              closeButton={{
                callback: onClose,
              }}
              submitButton={{
                disabled: isSubmitting || !dirty || isValidating,
                callback: () => {
                  handleSubmit();
                },
              }}
              errors={errors}
            />

            <DivisionConfirmDialog />
            <GroupConfirmDialog />
          </Form>
        );
      }}
    </Formik>
  );
};
